/**
 * Sign in (Figma 10 · Sign in 01–08). One form; every way it can go wrong is a
 * notice above it, and nothing typed is lost. `?redirect=` names where signing
 * in lands; `?reason=` says why a session ended (B4).
 */
import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Clock, MonitorSmartphone } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { PublicShell } from "@/components/gettingIn/PublicShell";
import {
  Column,
  GiButton,
  GiField,
  GoogleButton,
  Greeting,
  Head,
  Notice,
  OrDivider,
  QuietAction,
  Ways,
} from "@/components/gettingIn/parts";
import { LockedNotice, SignInNoticeView } from "@/components/gettingIn/SignInNotice";
import { useSignIn } from "@/components/gettingIn/useSignIn";
import { destinationName, rememberDestination, safeRedirect, type Session } from "@/components/gettingIn/authApi";

export default function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const destination = safeRedirect(params.get("redirect"));
  const reason = params.get("reason");
  const away = Number(params.get("away"));
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");

  const signedIn = (session: Session) => {
    login(session.token, session.refreshToken ?? "");
    navigate(destination ?? "/dashboard", { replace: true });
  };

  const passwordRef = useRef<HTMLInputElement>(null);
  const form = useSignIn({
    passwordRef,
    onSignedIn: signedIn,
    onNotVerified: (address) => {
      rememberDestination(destination);
      navigate(`/verify?email=${encodeURIComponent(address || email)}`);
    },
  });

  const placeName = destination ? destinationName(destination) : null;
  const busy = form.pending !== null;

  // Signed-out reasons show until the person does something that answers.
  const reasonNotice =
    !form.notice && reason === "locked" ? (
      <LockedNotice email={email || undefined} />
    ) : !form.notice && reason === "revoked" ? (
      <Notice tone="info" icon={MonitorSmartphone}>
        You were signed out from another device. Sign in to carry on.
      </Notice>
    ) : !form.notice && reason === "expired" ? (
      <Notice tone="info" icon={Clock}>
        {away > 0 ? `You were away ${away} ${away === 1 ? "day" : "days"}, so we signed you out.` : "You were signed out after a long time away."}{" "}
        Sign in to land where you were.
      </Notice>
    ) : null;

  return (
    <PublicShell still="sitting" title="Sign in">
      <Column>
        <Greeting />
        <Head title="Sign in" sub={placeName ? `Sign in and you land on ${placeName}.` : undefined} />
        <SignInNoticeView notice={form.notice} />
        {reasonNotice}
        <form
          className="flex w-full flex-col gap-6"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!email.trim() || !password) return;
            void form.signIn(email, password);
          }}
        >
          <div className="flex w-full flex-col gap-4">
            <GiField
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@studio.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={busy}
              required
            />
            <GiField
              ref={passwordRef}
              label="Password"
              secret
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              readOnly={busy}
              required
            />
            <div className="flex w-full justify-end">
              <QuietAction to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}>
                Forgot password?
              </QuietAction>
            </div>
          </div>
          <Ways>
            <GiButton type="submit" loading={form.pending === "password"} disabled={form.waiting || busy}>
              {form.notice?.kind === "offline" ? "Try again" : "Sign in"}
            </GiButton>
            <OrDivider />
            <GoogleButton
              type="button"
              onClick={form.google}
              loading={form.pending === "google"}
              disabled={busy}
            />
          </Ways>
        </form>
        <div className="flex w-full flex-col items-center">
          <p className="text-[12.5px] leading-[19px] text-co-muted">New here? Creo is by invitation.</p>
          <QuietAction to={`/signup${destination ? `?redirect=${encodeURIComponent(destination)}` : ""}`}>
            Create account
          </QuietAction>
        </div>
      </Column>
    </PublicShell>
  );
}
